import { Injectable, inject } from "@angular/core";
import { Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { UserAssignmentType } from "@core/enums/user-assignment-type.enum";
import { CourseNavigationOriginTypeEnum } from "@core/enums/course-navigation-origin-type.enum";
import type { UserModel } from "@core/models/users/user.model";
import { SessionUserDataService } from "@shared/services/user-data/session-user-data.service";
import {
  CATALOGUE_HOME_ROUTE,
  INDUCTION_HOME_ROUTE,
  LEARNING_PATHS_HOME_ROUTE,
} from "@core/consts/assignment-page.constants";
import { CourseNavigationContextService } from "@core/services/course-navigation-context.service";
import type { CourseNavigationOrigin } from "@core/models/course-navigation-origin.model";
import { isValidCourseId } from "@core/utils/is-valid-course-id.util";

@Injectable({
  providedIn: "root",
})
export class UserAssignmentNavigationService {
  private readonly sessionUserDataService: SessionUserDataService = inject(SessionUserDataService);
  private readonly courseNavigationContext: CourseNavigationContextService = inject(CourseNavigationContextService);
  private readonly router: Router = inject(Router);

  defaultHomeRoute(assignmentType?: UserAssignmentType | null): string {
    if (assignmentType === UserAssignmentType.LEARNING_ROUTE) return LEARNING_PATHS_HOME_ROUTE;

    return CATALOGUE_HOME_ROUTE;
  }

  learningPathDetailRoute(learningPathId: number): string {
    return `${LEARNING_PATHS_HOME_ROUTE}/${learningPathId}`;
  }

  courseDetailsBackRoute(courseId: number): string {
    if (!isValidCourseId(courseId)) return this.defaultHomeRouteForCurrentUser();

    const origin: CourseNavigationOrigin | null = this.courseNavigationContext.getOrigin(courseId);

    if (origin === null) return this.defaultHomeRouteForCurrentUser();

    switch (origin.type) {
      case CourseNavigationOriginTypeEnum.LEARNING_ROUTE: return this.learningPathDetailRoute(origin.learningRouteId);
      case CourseNavigationOriginTypeEnum.CATALOGUE: return CATALOGUE_HOME_ROUTE;
      case CourseNavigationOriginTypeEnum.INDUCTION: return INDUCTION_HOME_ROUTE;
      case CourseNavigationOriginTypeEnum.LEARNING_PATHS_ASSIGNED: return LEARNING_PATHS_HOME_ROUTE;
    }
  }

  private defaultHomeRouteForCurrentUser(): string {
    return this.defaultHomeRoute(this.sessionUserDataService.user()?.assignmentType);
  }

  resolveDefaultHomeRoute$(): Observable<string> {
    return this.sessionUserDataService.loadUserData$().pipe(
      map((user: UserModel) => this.defaultHomeRoute(user.assignmentType)),
      catchError(() => of(this.defaultHomeRoute())),
    );
  }

  defaultHomeUrlTree(assignmentType?: UserAssignmentType | null): UrlTree {
    return this.router.parseUrl(this.defaultHomeRoute(assignmentType));
  }

  resolveDefaultHomeUrlTree$(): Observable<UrlTree> {
    return this.resolveDefaultHomeRoute$().pipe(
      map((home: string) => this.router.parseUrl(home)),
    );
  }

  canActivateAssignment$(expected: UserAssignmentType): Observable<boolean | UrlTree> {
    return this.sessionUserDataService.loadUserData$().pipe(
      first(),
      map((user: UserModel) => {
        const current: UserAssignmentType | undefined = user.assignmentType;

        if (current === expected || (current == null && expected === UserAssignmentType.COURSES)) {
          return true;
        }

        return this.defaultHomeUrlTree(current);
      }),
    );
  }
}
